import { alpha } from "@mui/material/styles";

/**
 * Maps the MUI theme + Restack tokens into global CSS variables so that
 * legacy CSS files and future components can share a single source of truth.
 */
export const createDesignTokenStyles = (theme) => {
  const tokens = theme.restack?.tokens ?? {};
  const surfaces = tokens.surfaces ?? {};
  const radii = tokens.radii ?? {};
  const elevation = tokens.elevation ?? {};
  const gradients = tokens.gradients ?? {};
  const palette = theme.palette;

  const borderBaseColor =
    theme.palette.mode === "dark" ? "#94a3b8" : "#0f172a";
  const borderHairlineOpacity =
    theme.palette.mode === "dark" ? 0.12 : 0.07;
  const borderSubtleOpacity =
    theme.palette.mode === "dark" ? 0.16 : 0.1;
  const borderStrongOpacity =
    theme.palette.mode === "dark" ? 0.24 : 0.16;

  const backgroundDefault = palette.background?.default ?? "#111827";
  const backgroundPaper = palette.background?.paper ?? backgroundDefault;
  const backgroundCard =
    palette.background?.card ?? surfaces.card ?? backgroundPaper;
  const hoverColor =
    palette.action?.hover ?? alpha(palette.primary?.main || "#ffffff", 0.08);
  const selectedColor =
    palette.action?.selected ?? alpha(palette.primary?.main || "#ffffff", 0.12);
  const divider =
    palette.divider ?? alpha(borderBaseColor, borderSubtleOpacity);
  const surfaceBorder =
    surfaces.border ?? alpha(borderBaseColor, borderSubtleOpacity);
  const overlayBase =
    theme.palette.mode === "dark"
      ? alpha("#ffffff", 0.08)
      : alpha("#0f172a", 0.06);
  const overlayStrong =
    theme.palette.mode === "dark"
      ? alpha("#ffffff", 0.14)
      : alpha("#0f172a", 0.12);
  const errorColor = palette.error?.main ?? "#ef4444";
  const errorSurface = alpha(errorColor, theme.palette.mode === "dark" ? 0.18 : 0.12);
  const errorSurfaceStrong = alpha(errorColor, theme.palette.mode === "dark" ? 0.26 : 0.18);
  const errorBorderColor = alpha(errorColor, theme.palette.mode === "dark" ? 0.45 : 0.28);
  const errorBorderColorStrong = alpha(errorColor, theme.palette.mode === "dark" ? 0.55 : 0.36);
  const errorColorMuted = alpha(errorColor, 0.6);
  const errorColorFaint = alpha(errorColor, 0.4);
  const shadowPrimary =
    elevation.md ??
    (theme.palette.mode === "dark"
      ? "0px 18px 40px rgba(0, 0, 0, 0.28)"
      : "0px 18px 40px rgba(15, 23, 42, 0.16)");

  const shadowStrong =
    elevation.sm ??
    (theme.palette.mode === "dark"
      ? "0px 12px 32px rgba(0, 0, 0, 0.24)"
      : "0px 12px 32px rgba(15, 23, 42, 0.12)");

  const primaryGradient =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.05)";

  const gradientStops = tokens.gradients ?? {};
  const heroGradients = [
    ["heroWarm", gradientStops.heroWarm],
    ["heroUpload", gradientStops.heroUpload],
    ["heroCollab", gradientStops.heroCollab],
    ["heroReady", gradientStops.heroReady],
  ].reduce((acc, [key, stops]) => {
    if (Array.isArray(stops)) {
      const kebabKey = key
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .toLowerCase();
      acc[`--gradient-${kebabKey}-start`] = stops[0];
      acc[`--gradient-${kebabKey}-end`] = stops[1];
      acc[`--gradient-${kebabKey}`] = `linear-gradient(180deg, ${stops[0]} 0%, ${stops[1]} 100%)`;
    }
    return acc;
  }, {});

  return {
    ":root": {
      "--font-family-sans":
        theme.typography?.fontFamily ??
        '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      /* Core colors */
      "--background-default": backgroundDefault,
      "--background-paper": backgroundPaper,
      "--background-card": backgroundCard,
      "--background-elevated": surfaces.raised ?? backgroundPaper,
      "--text-primary": palette.text?.primary ?? "#ffffff",
      "--text-secondary": palette.text?.secondary ?? alpha("#ffffff", 0.7),
      "--divider": divider,
      "--action-hover": hoverColor,
      "--action-selected": selectedColor,
      "--card-border": surfaceBorder,
      "--surface-border": surfaceBorder,
      "--primary-color": palette.primary?.main ?? "#ffffff",
      "--icon-primary": palette.text?.primary ?? "#ffffff",
      "--icon-secondary": palette.text?.secondary ?? alpha("#ffffff", 0.6),
      "--focus-ring": palette.primary?.main ?? "#ffffff",
      "--primary-gradient": primaryGradient,
      "--surface-gradient": gradients.soft ?? primaryGradient,
      "--surface-hover": tokens.surfaces?.hover ?? hoverColor,
      "--surface-overlay": overlayBase,
      "--surface-overlay-strong": overlayStrong,
      "--surface-raised": tokens.surfaces?.raised ?? backgroundPaper,
      "--success-color": palette.success?.main ?? "rgba(255, 255, 255, 0.75)",
      "--warning-color": palette.warning?.main ?? "rgba(255, 255, 255, 0.6)",
      "--error-color": palette.error?.main ?? "rgba(255, 100, 100, 0.9)",
      "--error-color-muted": errorColorMuted,
      "--error-color-faint": errorColorFaint,
      "--error-surface": errorSurface,
      "--error-surface-strong": errorSurfaceStrong,
      "--error-border": `1px solid ${errorBorderColor}`,
      "--error-border-strong": `1px solid ${errorBorderColorStrong}`,
      "--error-border-color": errorBorderColor,
      "--error-border-color-strong": errorBorderColorStrong,
      "--info-color": palette.info?.main ?? "rgba(255, 255, 255, 0.6)",
      "--border-hairline":
        tokens.borders?.hairline ??
        `1px solid ${alpha(borderBaseColor, borderHairlineOpacity)}`,
      "--border-subtle":
        tokens.borders?.subtle ??
        `1px solid ${alpha(borderBaseColor, borderSubtleOpacity)}`,
      "--border-strong":
        tokens.borders?.strong ??
        `1px solid ${alpha(borderBaseColor, borderStrongOpacity)}`,

      /* Radii */
      "--surface-radius": `${radii.lg ?? theme.shape?.borderRadius ?? 20}px`,
      "--surface-radius-md": `${radii.md ?? theme.shape?.borderRadius ?? 16}px`,
      "--surface-radius-sm": `${radii.sm ?? 12}px`,
      "--surface-radius-xs": `${radii.xs ?? 8}px`,

      /* Shadows */
      "--surface-shadow": shadowPrimary,
      "--surface-shadow-strong": shadowStrong,
      "--shadow-color":
        theme.palette.mode === "dark"
          ? "rgba(0, 0, 0, 0.35)"
          : "rgba(15, 23, 42, 0.18)",

      /* Scrollbars */
      "--scrollbar-thumb":
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.16)"
          : "rgba(15, 23, 42, 0.16)",
      "--scrollbar-thumb-hover":
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.28)"
          : "rgba(15, 23, 42, 0.28)",

      /* Layout */
      "--rs-layout-appbar-height": "64px",
      ...heroGradients,
    },
    "@media (max-width:600px)": {
      ":root": {
        "--rs-layout-appbar-height": "56px",
      },
    },
  };
};
